<script setup lang="ts">
import "core-js/stable";
import "regenerator-runtime/runtime";
import PromotionalToast from "~/components/global/kentico/components/PromotionalToast.vue";
import SessionLimitModal from "~/components/layout/Account/ResponsibleGaming/Limits/SessionLimitModal.vue";
import SEO from "~/components/layout/SEO.vue";
import { useSessionLimit } from "~/composables/useSessionLimit";
import { useSettings } from "~/store/settings";
import DevelopmentDashboard from "./components/DevelopmentDashboard.vue";
import CookiePolicy from "./components/layout/CookiePolicy.vue";
import LiveChatLoadingOverlay from "./components/layout/LiveChatLoadingOverlay.vue";
import MarketingOptin from "./components/layout/MarketingOptin.vue";
import NativeBetslipPreviewHandlers from "./components/layout/NativeBetslipPreviewHandlers.vue";
import Notifications from "./components/layout/Notifications.vue";
import { useAuth } from "./store/auth";
import SwipeBetModal from "./components/layout/SwipeBet/SwipeBetModal.vue";
import { useQuery } from "@tanstack/vue-query";
import api from "./utils/auth/api";
import detectminiprogram from "./utils/detect-mini-program";
import initialiseAuth from "./utils/initialise/initialiseAuth";
import initialiseVertical from "./utils/initialise/initialiseVertical";
import fetchConfig from "./utils/initialise/fetchConfig";
import fetchSitemap from "./utils/initialise/fetchSitemap";
import fetchTranslations from "./utils/initialise/fetchTranslations";
import checkDatafreeCap from "./utils/initialise/checkDatefreeCap";
import initialiseLocale from "./utils/initialise-locale";
import initialiseNativeAppCookies from "./utils/initialise/initialiseNativeAppCookies";

const store = useSettings();
const config = useRuntimeConfig();
const auth = useAuth();
const route = useRoute();
const router = useRouter();
const settings = useSettings();
const isCollapsedView = useCollapsedView();
const showSessionModal = useSessionLimit();
const { startAuthCheck, stopAuthCheck } = useSessionCheck();
const simpleViewCookie = useCookie("betway-simple-view");
const stickyVerticalCookie = useCookie("sticky-vertical");
const gameSession = useCookie<{ route: string; imageUrl: string; name: string } | undefined>("previousGameSession");
const headers = useRequestHeaders(["user-agent"]);
const { isHomePage } = useHomePage();
const { $messageHandler, $trackEvent } = useNuxtApp();

const recaptchaEnabled = computed(() => {
	return settings.hasFeature("GoogleReCaptchaV2Enabled") && settings.config?.devConfig.googleCaptchaV3SiteKey;
});

const shouldFetchMyBetCount = computed(() => {
	return !!auth.hydrated && auth.loggedIn;
});

const collapseNav = computed(() => {
	return route.name === "event-sport-region-league-event" && isMobile && store.hasFeature("IsCollapsedViewEnabled") && !store.simpleView;
});

const enforceRegistration = computed(
	() => auth.loggedIn && store.hasFeature("IsUpdateDetailsRegEnabled") && !auth.profile?.metaData.firstName && !auth.profile?.metaData.lastName
);

declare global {
	interface Window {
		grecaptcha: {
			ready: (arg: any) => void;
			execute: (key: string, action: { action: string }) => Promise<string>;
		};
		ProfileInit: () => void;
		thkdlhgd?: { prgssdw: (E: string, Y: string, u: string, s: string) => void };
		__CONNEXONE_LIVECHAT__: {
			openWidget: () => void;
			closeWidget: () => void;
			authenticateCustomer: (args: { firstname: string | undefined; tel1: string | undefined; lead_reference: string | undefined }) => void;
		};
		__CONNEXONE_LIVECHAT_HOOKS__: {
			onLoad: () => void;
			onWidgetClosed: () => void;
			onWidgetHidden: () => void;
			onWidgetOpened: () => void;
			onWidgetShown: () => void;
		};
	}
}

watchEffect(() => {
	if (store.locale && store.getCountryConfig) {
		useHead({
			htmlAttrs: {
				lang: store.locale && store.getCountryConfig ? store.locale.replace("US", store.getCountryConfig.countryIsoTwo.toString()) : "en-US",
			},
		});
	}
});

watchEffect(() => {
	if (recaptchaEnabled.value) {
		useHead({
			script: [
				{
					src: `https://www.google.com/recaptcha/api.js?render=${settings.config?.devConfig.googleCaptchaV3SiteKey}`,
					defer: true,
					async: true,
				},
			],
		});
	}

	if (!settings.isDataFree) {
		useHead({
			script: [
				{
					async: true,
					defer: true,
					innerHTML: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
						new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
						j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.defer=true;j.src=
						'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
						})(window,document,'script','dataLayer','GTM-59D9F64');`,
				},
				{
					src: "https://widgets.betwayafrica.com/bannerwidget/bannermanagement/bannermanagement.esm.js",
					type: "module",
				},
				{
					src: "https://widgets.betwayafrica.com/influencer-widget/influencer-widget/influencer-widget.esm.js",
					type: "module",
				},
				{
					src: "https://nb1.api-gaming-engine.com/components/LotteryProductComponents.min.js",
				},
			],
		});
	}
});

router.afterEach((to, from) => {
	if (auth.hydrated && auth.loggedIn && (from.path.includes("/game/") || store.isDataFree)) {
		auth.updateBalance();
	}
	$trackEvent.loadPage({ path: window.location.pathname, url: window.location.href, title: document.title });
});

useQuery({
	queryKey: [auth.betCount],
	queryFn: () =>
		api<{ numberOdOpenBets: number }>({
			url: "/Betting/OpenBetsIndicator",
			type: "universal",
			options: { method: "GET" },
		}).then((data) => {
			auth.betCount = data.numberOdOpenBets;

			useNuxtApp().$messageHandler({
				message: "MyBetsCount",
				data: auth.betCount,
			});

			return data;
		}),
	enabled: shouldFetchMyBetCount,
});

onMounted(async () => {
	try {
		await initialiseVertical();
		await fetchConfig();
		await initialiseLocale();
		await Promise.all([fetchSitemap(), fetchTranslations()]).finally(async () => {
			// Simple View
			if (simpleViewCookie.value) {
				store.simpleView = Boolean(simpleViewCookie.value);
			}

			if (
				headers["user-agent"] &&
				isMobileByUserAgent(headers["user-agent"]) &&
				store.config?.appSetting?.SportsBookView === "Simple" &&
				Boolean(simpleViewCookie.value) !== false
			) {
				store.simpleView = true;
			}

			// Initialize Session Metadata
			if (!sessionStorage.getItem("sessionMetadata")) {
				sessionStorage.setItem("sessionMetadata", "{}");
			}

			//Set/Set Native App Cookies
			initialiseNativeAppCookies();

			// Default to casino if sports is down
			if (store.hasFeature("IsSplashPageEnabled") && route.path === "/" && !store.isDataFree) {
				const hasInstantGames = () => {
					return !!store.sitemap?.find((item) => item.A === "Instant-Games");
				};
				hasInstantGames() ? router.push("/lobby/instantgames") : router.push("/lobby/casino");
			}

			if (!stickyVerticalCookie.value?.includes("lobby")) gameSession.value = undefined;

			settings.isAppInitialised = true;
			initialiseAuth();
		});

		if (route.query.shared) {
			store.sharedPostModal = true;
		}

		detectminiprogram();
		startAuthCheck();
		initialiseThreatmetrics();
		if (!store.isDataFree) checkForVuvuBookingCode();
	} catch (e) {
		settings.isAppInitialised = true;
		console.log(e);
	}
});

const checkForVuvuBookingCode = () => {
	const source = route.query?.source;
	const code = typeof source === "string" && source.startsWith("/bookabet/") ? source.split("/bookabet/")[1] : null;

	if (code) {
		router.push("/sport/soccer/?bookingCode=" + code);
	}
};

watchEffect(() => {
	if (isCollapsedView.value || isHomePage.value || collapseNav.value) {
		$messageHandler({
			message: "NavStatus",
			data: {
				visible: false,
			},
		});
	} else {
		$messageHandler({
			message: "NavStatus",
			data: {
				visible: true,
			},
		});
	}
});

onUnmounted(() => {
	stopAuthCheck();
});

watchEffect(() => {
	if (auth.loggedIn === true) {
		if (store.isDataFree) checkDatafreeCap();

		if (enforceRegistration.value) {
			auth.modals.register = true;
		}
	}
});
</script>

<template>
	<NuxtLayout :name="store.isDataFree ? 'datafree' : 'default'" :class="route.name === 'swipe-bet' ? 'overflow-x-hidden' : ''">
		<SEO />
		<MarketingOptin />
		<CookiePolicy v-if="!store.isNativeApp" />
		<NuxtPage />
		<SessionLimitModal v-if="showSessionModal" />
		<ClientOnly>
			<NativeBetslipPreviewHandlers v-if="store.isNativeApp" />
			<DataFreeGuard>
				<Notifications v-if="auth.loggedIn" />
			</DataFreeGuard>
			<DataFreeGuard>
				<PromotionalToast />
			</DataFreeGuard>
		</ClientOnly>
		<UiToast />
		<DevelopmentDashboard v-if="['development', 'QA', 'UAT'].includes(config.public.env)" />
		<LiveChatLoadingOverlay v-if="route.query.livechat" />
		<SwipeBetModal />
	</NuxtLayout>
</template>
